import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import Constants from '@/constants';

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'touchstart',
] as const;

// scroll does not bubble, so it must be captured at the document level to catch
// scrolling inside child elements (e.g. a scrollable content pane).
const CAPTURE_ACTIVITY_EVENTS = ['scroll'] as const;

/** Brightcove iframe embeds cannot emit timeupdate to the parent document. */
const BRIGHTCOVE_IFRAME_SELECTOR = 'iframe[src*="players.brightcove.net"]';

const hasBrightcoveIframe = (): boolean =>
  document.querySelector(BRIGHTCOVE_IFRAME_SELECTOR) !== null;

// Stable subscription function for useSyncExternalStore — defined at module
// level so the reference is the same on every render.
const subscribeToVisibility = (callback: () => void): (() => void) => {
  document.addEventListener('visibilitychange', callback);
  return () => document.removeEventListener('visibilitychange', callback);
};

/**
 * Returns true when heartbeat intervals should be suspended.
 *
 * Two independent sources — suspended when either is true:
 *   - Tab hidden: the user switched tabs, minimized, or locked their screen.
 *     Exception: if the tab hides within TASK_LINK_GRACE_MS of a target="_blank"
 *     click (a task link opening a new tab), suspension is skipped.
 *   - Idle: no user interaction for IDLE_TIMEOUT_MS.
 *     Exception: video playback counts as activity.
 *     - In-page video (native <video> / Brightcove video-js): `timeupdate`
 *       fires while playing and resets the idle timer via document capture
 *       (event does not bubble). When playback pauses or ends `timeupdate`
 *       stops, so the idle timer runs out normally and suspension resumes.
 *     - Brightcove iframe fallback (`players.brightcove.net`): in-page embed
 *       is preferred, but when the player falls back to an iframe (unparseable
 *       URL or script load failure) media events never reach this document.
 *       While such an iframe is present, idle suspension is skipped
 *       (PATHWAY-3815 Option C). Tab-hidden suspension still applies.
 *     - YouTube iframes: YouTube sends postMessage events to the parent window
 *       while playing (infoDelivery) and on state changes (onStateChange info=1).
 *       Requires `enablejsapi=1` in the embed URL and a listening handshake on load.
 *
 * Tab visibility is tracked via useSyncExternalStore (always subscribed).
 * All other listeners are registered only while isActive is true.
 */
export const useHeartbeatSuspension = (isActive: boolean): boolean => {
  const rawTabHidden = useSyncExternalStore(
    subscribeToVisibility,
    () => document.hidden,
  );
  const [idle, setIdle] = useState(false);
  // True when the tab hid because a task link opened a new tab. Cleared on
  // the next visibilitychange (user returns) or on deactivation/reactivation.
  const [graceExempt, setGraceExempt] = useState(false);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskLinkClickedAtRef = useRef<number | null>(null);
  // True while a Brightcove iframe fallback is in the document — idle timeout
  // is skipped because media events never cross the iframe boundary.
  const skipIdleForBrightcoveIframeRef = useRef(false);

  useEffect(() => {
    if (!isActive) return;

    const { IDLE_TIMEOUT_MS, TASK_LINK_GRACE_MS } =
      Constants.VIEW_TIME_DEFAULTS;

    const clearIdleTimer = () => {
      if (idleTimerRef.current !== null) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const startIdleTimer = () => {
      if (skipIdleForBrightcoveIframeRef.current) return;
      clearIdleTimer();
      idleTimerRef.current = setTimeout(() => {
        setIdle(true);
      }, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      setIdle(false);
      startIdleTimer();
    };

    const syncBrightcoveIframeSkip = () => {
      const present = hasBrightcoveIframe();
      const wasSkipping = skipIdleForBrightcoveIframeRef.current;
      if (present === wasSkipping) {
        return;
      }

      skipIdleForBrightcoveIframeRef.current = present;
      if (present) {
        setIdle(false);
        clearIdleTimer();
      } else {
        startIdleTimer();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        const clickedAt = taskLinkClickedAtRef.current;
        if (clickedAt !== null && Date.now() - clickedAt < TASK_LINK_GRACE_MS) {
          // Tab hid because a task link opened a new tab — don't suspend.
          setGraceExempt(true);
        }
      } else {
        setGraceExempt(false);
      }
    };

    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof Element && target.closest('a[target="_blank"]')) {
        taskLinkClickedAtRef.current = Date.now();
      }
    };

    // YouTube iframes send postMessage events to the parent window while playing.
    // infoDelivery fires periodically with currentTime; onStateChange info=1 means
    // playing. Requires enablejsapi=1 in the embed URL plus a listening handshake
    // sent to the iframe on load (see subscribeYouTubeEmbedPostMessages).
    //
    // Brightcove uses in-page embeds so media events reach the document-level
    // timeupdate capture listener below. Iframe fallbacks are handled by
    // syncBrightcoveIframeSkip (MutationObserver) instead.
    const handleWindowMessage = (e: MessageEvent) => {
      try {
        const data: unknown =
          typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data === null || typeof data !== 'object' || !('event' in data)) {
          return;
        }

        if (e.origin === 'https://www.youtube.com') {
          if (
            data.event === 'infoDelivery' ||
            (data.event === 'onStateChange' &&
              'info' in data &&
              data.info === 1)
          ) {
            handleActivity();
          }
        }
      } catch {
        // Ignore unparseable messages.
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('click', handleAnchorClick, true);
    // timeupdate doesn't bubble, so capture is required to catch it from any
    // descendant <video> element embedded directly in the document.
    document.addEventListener('timeupdate', handleActivity, {
      capture: true,
      passive: true,
    });
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    CAPTURE_ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, {
        capture: true,
        passive: true,
      });
    });
    window.addEventListener('message', handleWindowMessage);

    const brightcoveIframeObserver = new MutationObserver(
      syncBrightcoveIframeSkip,
    );
    brightcoveIframeObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    });

    // Reset activation state — synchronizing with external state on effect
    // entry is the intended use-case for effects; these are not cascading renders.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIdle(false);

    setGraceExempt(false);
    skipIdleForBrightcoveIframeRef.current = false;
    if (hasBrightcoveIframe()) {
      skipIdleForBrightcoveIframeRef.current = true;
    } else {
      startIdleTimer();
    }

    return () => {
      clearIdleTimer();
      taskLinkClickedAtRef.current = null;
      skipIdleForBrightcoveIframeRef.current = false;
      brightcoveIframeObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('click', handleAnchorClick, true);
      document.removeEventListener('timeupdate', handleActivity, true);
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      CAPTURE_ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener('message', handleWindowMessage);
    };
  }, [isActive]);

  // graceExempt is true when the tab hid within the task-link grace window
  // (a target="_blank" click just opened a new tab). Cleared on next return.
  return isActive && ((rawTabHidden && !graceExempt) || idle);
};
